'use client'

import { useFormStore, useStoreState } from '@ariakit/react'
import { ComponentProps, ReactNode, useMemo, useState } from 'react'
import styled from 'styled-components'

import { LocaleButton, Skeleton } from '@syconium/little-miss-figgy'
import { FlagIconImage } from '@syconium/little-miss-figgy/dist/components/FlagIconImage'
import { ScreenReaderOnly } from '@syconium/little-miss-figgy/dist/components/ScreenReaderOnly'
import { Select, SelectOption } from '@syconium/little-miss-figgy/dist/components/Select/Select'
import { TextLink } from '@syconium/little-miss-figgy/dist/components/TextLink'
import { SupportedLocaleIdentifier, SupportedRegionIdentifier } from '@syconium/weeping-figs'

import { useTranslatedLocalization } from '../../_hooks/useTranslatedLocalization'
import { useTranslation } from '../../_providers/TranslationProvider.client'
import {
  NextDialogFooter,
  NextDialogHeader,
  NextDialogTitle,
  NextDialogTrigger,
} from '../dialogs/NextDialog.client'
import { Form } from '../form/Form'
import { FormSubmit } from '../form/FormSubmit'
import { NextLink } from '../navigation/NextLink'

const SkeletonLocalizationForm = () => {
  return (
    <>
      <FormFieldSkeleton />
      <FormFieldSkeleton />
      <FormFieldSkeleton excludeLabel />
    </>
  )
}

const LocalizationDialogHeader = styled(
  (props: Omit<ComponentProps<typeof NextDialogHeader>, 'closeText'>) => {
    const translations = useTranslation()
    const { data } = useTranslatedLocalization()
    const { region: currentRegion } = data
    const dialogText = translations.regionAndLocalePicker.modal
    const dialogTitleText = `${dialogText.title} ${dialogText.titleLanguage}`

    return (
      <NextDialogHeader {...props} closeText={translations.magnolia.general.close} borderless>
        <StyledHeader align='center'>
          <FlagIconImage alt='' src={currentRegion.flagSrc} />
          {dialogTitleText}
        </StyledHeader>
      </NextDialogHeader>
    )
  }
)`
  text-align: center;
  text-wrap: balance;
  justify-content: center;
`

const LocalizationDialogContentFooter = () => {
  const translations = useTranslation()
  return (
    <StyledFooterLinks>
      <TextLink as={NextLink} color='secondary' href='/pages/non-us-privacy-policy'>
        {translations.regionAndLocalePicker.modal.figsPrivacyLink}
      </TextLink>
      <TextLink
        color='secondary'
        href='https://s3.global-e.com/merchantscontent/Figs/docs/FIGS_Privacy_Policy--2022_06_03.pdf'
      >
        {translations.regionAndLocalePicker.modal.gePrivacyLink}
      </TextLink>
    </StyledFooterLinks>
  )
}

const LocalizationDialogContent = () => {
  const translations = useTranslation()
  const { data, status, changeLocale } = useTranslatedLocalization()
  const { regions, region: currentRegion, locale: currentLocale } = data
  const dialogText = translations.regionAndLocalePicker.modal
  const dialogTitleText = `${dialogText.title} ${dialogText.titleLanguage}`

  const [userSelectedRegion, setUserSelectedRegion] = useState<typeof currentRegion | null>(null)

  const regionOptions = useMemo(() => {
    return regions.filter(supportedRegion => {
      return supportedRegion.isEnabled || supportedRegion.id === currentRegion.id
    })
  }, [currentRegion.id, regions])

  const localeOptions = useMemo(
    () =>
      (userSelectedRegion ?? currentRegion).languagesArray.filter(
        regionalLanguage => regionalLanguage.isEnabled
      ),
    [userSelectedRegion, currentRegion]
  )

  const form = useFormStore({
    defaultValues: {
      region: currentRegion.id,
      locale: currentLocale,
    },
  })

  const regionValue = useStoreState(form, state => state.values.region)
  const localeValue = useStoreState(form, state => state.values.locale)
  const isSubmitting = useStoreState(form, state => state.submitting)

  form.useSubmit(state => {
    changeLocale(
      regionOptions.find(regionOption => regionOption.id === state.values.region) ??
        userSelectedRegion ??
        currentRegion,
      state.values.locale
    )
  })

  return (
    <>
      {status !== 'resolved' ? (
        <StyledLocalizationForm>
          <ScreenReaderOnly>{translations.magnolia.general.loading}</ScreenReaderOnly>
          <SkeletonLocalizationForm />
        </StyledLocalizationForm>
      ) : (
        <Form store={form} noValidate aria-label={dialogTitleText} resetOnSubmit={false}>
          <StyledLocalizationForm>
            <div>
              <Select
                label={dialogText.labelCountry}
                isInvalid={false}
                value={regionValue}
                onChange={newRegionId => {
                  form.setValue('region', newRegionId as SupportedRegionIdentifier)
                  const newSelectedRegion = regionOptions.find(r => r.id === newRegionId)
                  if (newSelectedRegion) {
                    form.setValue('locale', newSelectedRegion.defaultLocale)
                    setUserSelectedRegion(newSelectedRegion)
                  }
                }}
                renderSelectValue={selectedRegionId =>
                  regionOptions.find(regionOption => regionOption.id === selectedRegionId)?.name ??
                  selectedRegionId
                }
              >
                {regionOptions.map(regionOption => (
                  <SelectOption key={regionOption.id} value={regionOption.id}>
                    {regionOption.name}
                  </SelectOption>
                ))}
              </Select>
            </div>

            <div>
              <Select
                label={dialogText.labelLanguage}
                isInvalid={false}
                value={localeValue}
                onChange={newLocaleId => {
                  form.setValue('locale', newLocaleId as SupportedLocaleIdentifier)
                }}
                renderSelectValue={selectedLocaleId =>
                  localeOptions.find(localeOption => localeOption.locale === selectedLocaleId)
                    ?.name ?? selectedLocaleId
                }
              >
                {localeOptions.map(localeOption => (
                  <SelectOption key={localeOption.locale} value={localeOption.locale}>
                    {localeOption.name}
                  </SelectOption>
                ))}
              </Select>
            </div>

            <FormSubmit
              submittingLabel={translations.magnolia.general.submitting}
              isSubmitting={isSubmitting}
            >
              {dialogText.ctaText}
            </FormSubmit>
          </StyledLocalizationForm>
        </Form>
      )}
    </>
  )
}

const StyledLocalizationForm = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: ${o => o.theme.spacing(5)};
  text-align: left;
  min-width: 280px;
`

const LocalizationMenu = styled.div`
  display: flex;
  align-items: center;
  gap: ${o => o.theme.spacing(2)};
`

const StyledHeader = styled(NextDialogTitle)`
  display: flex;
  flex-direction: column;
  gap: ${o => o.theme.spacing(5)};
`

const StyledFooterLinks = styled(NextDialogFooter)`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${o => o.theme.spacing(4)};
  padding: ${o => o.theme.spacing(4, 6, 6, 6)};
  background-color: ${o => o.theme.color.fill.subtle};
`

const FormFieldSkeleton = (props: { excludeLabel?: boolean }) => {
  return (
    <div>
      {props.excludeLabel ? null : (
        <Skeleton animated height='16px' width='100px' style={{ marginBottom: '4px' }} />
      )}
      <Skeleton animated height='42px' width='100%' style={{ marginBottom: '0px' }} />
    </div>
  )
}

const PrimaryNavLocalizationTrigger = ({ children }: { children: ReactNode }) => (
  <NextDialogTrigger render={<LocaleButton isColorInverted={false} />}>
    {children}
  </NextDialogTrigger>
)

export {
  LocalizationDialogHeader as PrimaryNavLocalizationDialogHeader,
  LocalizationDialogContentFooter as PrimaryNavLocalizationDialogContentFooter,
  LocalizationDialogContent as PrimaryNavLocalizationDialogContent,
  LocalizationMenu as PrimaryNavLocalizationMenu,
  PrimaryNavLocalizationTrigger,
}
